import { and, eq } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { useRef } from 'react'
import { useAuth } from '@/lib/auth'
import { logAccessDenied } from '@/lib/errors'
import { useStore } from '@/lib/pocketbase'
import type { Organization, UserRole } from '@/lib/types'

export interface OrgBySlugResult {
    org: Organization | null
    role: UserRole | null
    userOrgId: string | null
    hasAccess: boolean
    isLoading: boolean
    workingHoursOverride: boolean
    workStartTime: string | null
    workEndTime: string | null
    workingDays: number[] | null
    trackingPausedUntil: string | null
    inactiveAt: string | null
}

interface LoadingStateParams {
    isAuthInitializing: boolean
    queryLoading: boolean
    userId: string | undefined
    orgSlug: string | null | undefined
    collectionsReady: boolean
    collectionsHaveData: boolean
    status: string
}

function computeLoadingState(params: LoadingStateParams): boolean {
    const {
        isAuthInitializing,
        queryLoading,
        userId,
        orgSlug,
        collectionsReady,
        collectionsHaveData,
        status,
    } = params
    if (isAuthInitializing || queryLoading) return true
    if (userId && !collectionsReady) return true
    if (status === 'disabled' && orgSlug && userId && !isAuthInitializing) return true
    return !!userId && collectionsReady && !collectionsHaveData && status !== 'ready'
}

type ExtendedUserOrg = {
    working_hours_override?: boolean
    work_start_time?: string
    work_end_time?: string
    working_days?: number[]
    tracking_paused_until?: string
    inactive_at?: string
}

export function useOrgBySlug(orgSlug: string | null | undefined): OrgBySlugResult {
    const authContext = useAuth({ throwIfAnon: false })
    const userId = authContext.user?.id
    const isAuthInitializing = authContext.isInitializing

    const [orgsCollection, userOrgCollection] = useStore('orgs', 'user_org')

    const {
        data: result,
        isLoading: queryLoading,
        status,
    } = useLiveQuery(
        q =>
            orgSlug && userId
                ? q
                      .from({ userOrg: userOrgCollection })
                      .join({ org: orgsCollection }, ({ org, userOrg }) =>
                          org ? eq(userOrg.org, org.id) : false
                      )
                      .where(({ userOrg, org }) =>
                          org ? and(eq(userOrg.user, userId), eq(org.slug, orgSlug)) : false
                      )
                      .select(({ org, userOrg }) => ({
                          org: org
                              ? {
                                    id: org.id,
                                    name: org.name,
                                    slug: org.slug,
                                    logo: (org as unknown as { logo?: string }).logo ?? null,
                                }
                              : null,
                          role: userOrg?.role,
                          userOrgId: userOrg?.id,
                          userOrg: userOrg,
                      }))
                      .findOne()
                : undefined,
        [orgSlug, userId, orgsCollection.isReady(), userOrgCollection.isReady()]
    )

    const collectionsReady = orgsCollection.isReady() && userOrgCollection.isReady()
    const collectionsHaveData = orgsCollection.size > 0 && userOrgCollection.size > 0
    const isLoading = computeLoadingState({
        isAuthInitializing,
        queryLoading,
        userId,
        orgSlug,
        collectionsReady,
        collectionsHaveData,
        status,
    })

    const extendedUserOrg = result?.userOrg as ExtendedUserOrg | undefined

    const hasAccess = !!result?.role && !extendedUserOrg?.inactive_at

    const accessDeniedReported = useRef(false)
    if (
        !isLoading &&
        !hasAccess &&
        userId &&
        orgSlug &&
        collectionsHaveData &&
        authContext.isLoggedIn &&
        !accessDeniedReported.current
    ) {
        accessDeniedReported.current = true
        logAccessDenied('useOrgBySlug', {
            orgSlug,
            userId,
            isAuthInitializing,
            queryLoading,
            queryStatus: status,
            collectionsReady,
            collectionsHaveData,
        })
    }

    return {
        org: result?.org ?? null,
        role: (result?.role as UserRole | undefined) ?? null,
        userOrgId: result?.userOrgId ?? null,
        hasAccess,
        isLoading,
        workingHoursOverride: extendedUserOrg?.working_hours_override ?? false,
        workStartTime: extendedUserOrg?.work_start_time ?? null,
        workEndTime: extendedUserOrg?.work_end_time ?? null,
        workingDays: extendedUserOrg?.working_days ?? null,
        trackingPausedUntil: extendedUserOrg?.tracking_paused_until ?? null,
        inactiveAt: extendedUserOrg?.inactive_at ?? null,
    }
}
